"use client";

import React from "react";
import { TriangleAlert, Clock, Loader, MapPin, Phone, CloudUpload } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
function EmergencyForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Card className="border-accent/20 bg-accent/5">
        <CardContent className="flex flex-col items-center justify-center py-12 text-center">
          <div className="mb-4 rounded-full bg-accent/20 p-4 text-accent">
            <TriangleAlert className="size-12" />
          </div>
          <h3 className="mb-2 text-2xl font-bold text-foreground">Request Received</h3>
          <p className="mb-6 text-muted-foreground">{successMessage}</p>
          <Button onClick={resetForm} variant="outline" size="lg" className="rounded-xl border-primary text-primary hover:bg-primary/5">
            Submit Another Request
          </Button>
        </CardContent>
      </Card>;
  }
  const inputBaseClasses = "flex w-full rounded-xl border border-input bg-background/50 px-4 py-3 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors";
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <FormField control={control} name="owner_name" rules={{
        required: "Your name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-base font-medium">Full Name</FormLabel>
              <FormControl>
                <input type="text" className={`${inputBaseClasses} h-14`} placeholder="John Smith" {...field} value={field.value || ""} />
              </FormControl>
              <FormDescription>Please enter your legal name for medical records.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="owner_phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^\+?[0-9\s\-\(\)]+$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-base font-medium">Phone Number</FormLabel>
              <FormControl>
                <input type="tel" className={`${inputBaseClasses} h-14 border-border focus-visible:ring-primary`} placeholder="(555) 000-0000" {...field} value={field.value || ""} />
              </FormControl>
              <FormDescription>A specialist will call you at this number.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <FormField control={control} name="owner_email" rules={{
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-base font-medium">Email Address</FormLabel>
              <FormControl>
                <input type="email" className={`${inputBaseClasses} h-14`} placeholder="john@example.com" {...field} value={field.value || ""} />
              </FormControl>
              <FormDescription>Used for appointment confirmation and HIPAA docs.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="location_zip" rules={{
        required: "Insurance provider is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-base font-medium">Insurance Provider</FormLabel>
              <FormControl>
                <input type="text" className={`${inputBaseClasses} h-14`} placeholder="e.g. BlueCross BlueShield" {...field} value={field.value || ""} />
              </FormControl>
              <FormDescription>We verify coverage before your consultation.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="dog_breed" rules={{
      required: "Primary symptom is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-base font-semibold text-primary">Primary Symptom</FormLabel>
            <FormControl>
              <input type="text" className={`${inputBaseClasses} h-16 text-lg`} placeholder="e.g. Loud snoring or daytime fatigue" {...field} value={field.value || ""} />
            </FormControl>
            <FormDescription>What is your main concern regarding your sleep?</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="emergency_details" rules={{
      required: "Please describe your symptoms"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-base font-semibold text-foreground">Describe Your Symptoms</FormLabel>
            <FormControl>
              <textarea className={`${inputBaseClasses} min-h-[160px] resize-y text-lg border-input focus-visible:ring-primary`} placeholder="How long have you noticed these issues? Do you wake up gasping?..." {...field} value={field.value || ""} />
            </FormControl>
            <FormDescription>Providing details helps us prepare for your call.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="photo_upload" render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem>
            <FormLabel className="text-base font-medium">Upload Recent Sleep Study (Optional)</FormLabel>
            <FormControl>
              <div className="relative flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-input bg-background/30 py-8 transition-colors hover:bg-accent/10">
                <CloudUpload className="mb-2 size-8 text-muted-foreground" />
                <span className="text-sm font-medium text-muted-foreground">Click to upload PDF or drag and drop</span>
                <input type="file" className="absolute inset-0 h-full w-full cursor-pointer opacity-0" onChange={e => onChange(e.target.files)} {...field} />
              </div>
            </FormControl>
            <FormDescription>Uploading an existing study can expedite your treatment.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          <p className="flex items-center gap-2 font-medium">
            <TriangleAlert className="size-5" />
            {submitError}
          </p>
        </div>}

      <Button type="submit" size="lg" disabled={isSubmitting} className="h-16 w-full rounded-xl bg-accent text-lg font-bold text-accent-foreground shadow-lg shadow-accent/20 hover:opacity-90">
        {isSubmitting ? <>
            <Loader className="mr-2 size-6 animate-spin" />
            Verifying Coverage...
          </> : "Verify My Coverage & Request Consultation"}
      </Button>
    </Form>;
}
export default function EmergencyHelpRequest() {
  return <section data-section-id="134297" id="protect-your-future" className="dark bg-primary text-primary-foreground relative overflow-hidden py-20 md:py-32">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-[20%] -right-[10%] h-[70%] w-[50%] rounded-full bg-accent/10 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] h-[70%] w-[50%] rounded-full bg-white/5 blur-[120px]" />
      </div>

      <div className="container relative mx-auto px-4 md:px-6">
        <div className="mb-12 text-center md:mb-20">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-accent/20 px-4 py-1.5 text-sm font-semibold text-accent">
            <span className="relative flex size-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75"></span>
              <span className="relative inline-flex size-2.5 rounded-full bg-accent"></span>
            </span>
            Board-Certified Sleep Specialists
          </div>
          <h2 className="text-4xl font-bold tracking-tight md:text-6xl lg:text-7xl">
            Protect Your Future
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-primary-foreground/80 md:text-xl">
            Untreated sleep apnea is linked to heart disease, stroke, and diabetes. Don't wait for a health crisis—take control of your sleep today.
          </p>
        </div>

        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 lg:grid-cols-[1fr_380px] lg:gap-16">
          <div className="w-full max-w-[650px] justify-self-center lg:justify-self-end">
            <Card className="border-border/20 bg-card/95 backdrop-blur-sm text-card-foreground shadow-2xl">
              <CardHeader className="pb-4">
                <CardTitle className="text-2xl font-bold">Request a Free Consultation</CardTitle>
                <CardDescription className="text-base">
                  Provide your details below to see if you qualify for medical insurance coverage.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormProvider formKey="protect_future_form_health_risks" formId="protect_future_form_health_risks" sectionName="Protect Your Future" defaultValues={{
                owner_name: "",
                owner_email: "",
                owner_phone: "",
                dog_breed: "",
                location_zip: "",
                emergency_details: "",
                photo_upload: undefined
              }} validationRules={{
                owner_name: {
                  required: true
                },
                owner_phone: {
                  required: true,
                  pattern: "phone"
                },
                dog_breed: {
                  required: true
                },
                location_zip: {
                  required: true
                },
                emergency_details: {
                  required: true
                }
              }} submitText="Verify My Coverage" successMessage="Thank you. A specialist will review your information and contact you shortly.">
                  <EmergencyForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

          <div className="w-full">
            <div className="sticky top-24 space-y-6">
              <Card className="overflow-hidden border-accent/30 bg-card text-card-foreground shadow-2xl shadow-accent/5">
                <div className="bg-accent/10 px-6 py-4">
                  <CardTitle className="flex items-center gap-2 text-xl text-primary">
                    <TriangleAlert className="size-6 text-accent" />
                    Speak with a Specialist
                  </CardTitle>
                </div>
                <CardContent className="pt-6">
                  <div className="mb-6 space-y-4">
                    <div>
                      <h4 className="text-lg font-bold text-foreground">Solutions for Sleep Apnea Clinic</h4>
                      <p className="mt-2 flex items-start gap-3 text-muted-foreground">
                        <MapPin className="mt-0.5 size-5 shrink-0 text-accent" />
                        <span>Available Nationwide<br />Telehealth & In-Person Options</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-3 rounded-lg bg-secondary p-3 text-sm font-medium text-foreground">
                      <Clock className="size-5 text-accent" />
                      Priority Response • HIPAA Compliant
                    </div>
                  </div>

                  <Button variant="default" size="lg" className="h-16 w-full rounded-xl bg-primary text-xl font-bold text-primary-foreground shadow-lg shadow-primary/20 transition-transform hover:scale-[1.02]" asChild>
                    <Link to="/FindASpecialistPage">
                      <Phone className="mr-3 size-6" />
                      Talk to a Specialist
                    </Link>
                  </Button>
                  <p className="mt-4 text-center text-xs text-muted-foreground">
                    Confidential evaluation based on medical standards.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-border/20 bg-card/90 backdrop-blur-sm text-card-foreground">
                <CardContent className="p-6">
                  <h4 className="mb-3 font-semibold text-foreground">When to seek a consultation:</h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <div className="mt-1.5 size-1.5 rounded-full bg-accent shrink-0" />
                      Loud, chronic snoring that disturbs others
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="mt-1.5 size-1.5 rounded-full bg-accent shrink-0" />
                      Waking up gasping or choking for air
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="mt-1.5 size-1.5 rounded-full bg-accent shrink-0" />
                      Excessive daytime sleepiness or fatigue
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="mt-1.5 size-1.5 rounded-full bg-accent shrink-0" />
                      Morning headaches or mood changes
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>;
}